import * as React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import withLoader from 'hoc/witLoader';
import { ActionCloseReason } from 'constants/CloseReasonTypes';
import { styled } from '@mui/material/styles';
import { v4 as uuidv4 } from 'uuid';
import CommandDataGrid from './grid/CommandDataGrid';
import DrawerBottom from './layout/DrawerBottom';
import { TableActionsTypes } from 'constants/TableActionsTypes';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import createFeatureCollection from 'components/map/helpers/createFeatureCollection';
import DrawerRight from './layout/DrawerRight';
import MissionFullScreenDialog from './layout/MissionFullScreenDialog';
import MissionActions from '../MissionActions';
import PointMissionService from 'services/mission/PointMissionService';
import PointMissionConfig from 'pages/mission/config/forms/PointMissionConfig';
import { Point } from 'models/mission/Point';
import MissionToolbar from './layout/MissionToolbar';
import MissionMap from './layout/MissionMap';
import MissionService from 'services/mission/MissionService';
import { useSnackbar } from 'notistack';
import EditMissionPointModalForm from './grid/EditMissionPointModalForm';

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: prop => prop !== 'open' })(
    ({ theme }) => ({
        flexGrow: 1,
        padding: theme.spacing(3),
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        marginRight: 0,
        position: 'relative',
        variants: [
            {
                props: ({ open }) => open,
                style: {
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.easeOut,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    marginRight: `${drawerWidth - 16}px`,
                },
            },
        ],
    }),
);

const MissionForm = ({
    title,
    formConfig,
    slice,
    defaultValue = {},
    onClose = () => {},
}) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [openFullScreen, setOpenFullScreen] = React.useState(true);
    const [open, setOpen] = React.useState(false);
    const [openRightDrawer, setOpenRightDrawer] = React.useState(false);
    const [openBottomDrawer, setOpenBottomDrawer] = React.useState(false);
    const dataMission = useSelector(state => state[slice]?.mission);

    const handleRightDrawer = () => {
        setOpenRightDrawer(!openRightDrawer);
    };

    const handleBottomDrawer = () => {
        setOpenBottomDrawer(!openBottomDrawer);
    };

    const handleOnClose = action => {
        setOpen(false);
    };

    const missionStepsUpdate = React.useCallback(
        async steps => {
            if (!steps) return;
            const updatedMission = { ...dataMission, steps: steps };
            dispatch(MissionService.updateMission(updatedMission))
                .then(response => {
                    enqueueSnackbar('Data saved', { variant: 'success' });
                })
                .catch(error => {
                    enqueueSnackbar(error.message, { variant: 'error' });
                });
        },
        [dataMission, dispatch, enqueueSnackbar],
    );

    const handleChangeGeoJSON = async data => {
        console.log(data);
        let dataFeatures =
            data.features.length > 0
                ? data.features.map(feature => {
                      let points = [];
                      feature.geometry.coordinates.forEach((item, i) => {
                          let dataMissionItem = {
                              ...(dataMission?.points[i] || []),
                              coordinates: item,
                              lat: item[0],
                              lon: item[1],
                              id: !dataMission?.points[i]
                                  ? uuidv4()
                                  : dataMission?.points[i].id,
                          };
                          points.push(dataMissionItem);
                      });
                      return {
                          ...feature,
                          properties: { ...feature.properties, items: points },
                      };
                  })[0]?.properties?.items
                : [];
        let steps = dataMission.steps.filter(step => !step.lat && !step.lon);
        dataFeatures = [...steps, ...dataFeatures]
            .sort((a, b) => a.ordinal - b.ordinal)
            .map((item, index) => {
                return {
                    ...item,
                    ordinal: index + 1,
                };
            });

        // Only call missionStepsUpdate, it will handle both dispatch and API call
        missionStepsUpdate(dataFeatures);
    };

    const handleToolbarClose = action => {
        setOpenFullScreen(false);
        onClose(action);
    };

    const handleCommandDataGridUpdate = commands => {
        if (!commands || commands.length === 0) return;
        missionStepsUpdate(commands?.length > 0 ? commands : []);
    };

    const handleAddCommand = (command, successCallback, errorCallback) => {
        if (!command) return;
        let mission = { ...dataMission, steps: [...dataMission.steps] };
        command = {
            ...command,
            id: uuidv4(),
            ordinal: command.ordinal || mission.steps.length + 1,
        };

        const updateOrdinal = array => {
            return array.length === 0
                ? array
                : array.map((item, i) => {
                      return { ...item, ordinal: i + 1 };
                  });
        };

        switch (+command.ordinal) {
            case 1:
                mission.steps.unshift(command);
                break;
            case command.ordinal > mission.steps.length:
                mission.steps.push(command);
                break;
            default:
                mission.steps.splice(+command.ordinal - 1, 0, command);
                break;
        }
        try {
            dispatch(
                MissionService.updateMission({
                    ...mission,
                    steps: updateOrdinal(mission.steps),
                }),
            );
            successCallback('Data saved');
        } catch (err) {
            errorCallback(err, 'An error occurred');
        }
    };

    const updateCommandMission = command => {
        if (!command || !dataMission) return;
        missionStepsUpdate(
            dataMission.steps.map(item =>
                command.id === item.id ? command : item,
            ),
        );
    };

    const removeElementAndUpdateOrdinal = (array, id) => {
        const index = array.findIndex(item => item.id === id);
        if (index !== -1) {
            array.splice(index, 1);
            return array.length === 0
                ? array
                : array.map((item, i) => {
                      return { ...item, ordinal: i + 1 };
                  });
        }
        return array;
    };

    const handleCommandDelete = id => {
        if (!id) return;
        missionStepsUpdate(
            removeElementAndUpdateOrdinal([...dataMission.steps], id),
        );
    };

    return (
        <MissionFullScreenDialog
            open={openFullScreen}
            toolbar={
                <MissionToolbar
                    onClose={() => handleToolbarClose(ActionCloseReason.Submit)}
                    onDrawer={handleRightDrawer}
                    openDrawer={openRightDrawer}
                    title={title}
                    dataMission={dataMission}
                    formConfig={formConfig}
                />
            }
        >
            <Box>
                <Main
                    open={openRightDrawer}
                    sx={{
                        height: 'calc( 100vh - 60px)',
                        display: 'grid',
                        gridTemplateRows: '1fr auto',
                    }}
                >
                    <MissionMap
                        sx={{ height: '100%' }}
                        // center={null}
                        // zoom={MAP_CONFIG.zoom}
                        onChangeGeoJSON={handleChangeGeoJSON}
                        geoJSON={
                            createFeatureCollection(
                                JSON.parse(JSON.stringify(dataMission))?.points,
                            ) || ''
                        }
                    />
                    {/* Bottom Drawer */}
                    <DrawerBottom
                        open={openBottomDrawer}
                        openRightDrawer={openRightDrawer}
                        title={'Commands'}
                        slice={'miss'}
                        rightDrawerWidth={drawerWidth}
                        onDrawer={handleBottomDrawer}
                    >
                        <CommandDataGrid
                            basePath="/mission"
                            service={MissionService}
                            config={PointMissionConfig}
                            // toolbarActionConfig={}
                            slice={slice}
                            tableActionConfig={[
                                {
                                    CustomComponent: EditMissionPointModalForm,
                                    type: TableActionsTypes.CustomEdit,
                                    service: PointMissionService,
                                    config: PointMissionConfig,
                                    entity: Point,
                                    open: open,
                                    onClose: handleOnClose,
                                    actionType: updateCommandMission,
                                },
                                {
                                    type: TableActionsTypes.DeleteWithAction,
                                    actionType: handleCommandDelete,
                                    service: PointMissionService,
                                },
                            ]}
                            data={dataMission?.steps}
                            onAddCommand={handleAddCommand}
                            onCommandUpdate={handleCommandDataGridUpdate}
                            // sx={{ maxHeight: `${heightWindow * 0.3}px` }}
                        />
                    </DrawerBottom>
                </Main>
                {/* Right Drawer */}
                <DrawerRight
                    open={openRightDrawer}
                    //  openRightDrawer={openRightDrawer}
                    title={'Actions'}
                    slice={'rightDrawer'}
                    rightDrawerWidth={drawerWidth}
                    onDrawer={handleRightDrawer}
                >
                    <MissionActions data={dataMission} />
                </DrawerRight>
            </Box>
        </MissionFullScreenDialog>
    );
};

MissionForm.propTypes = {
    title: PropTypes.string.isRequired,
    formConfig: PropTypes.arrayOf(
        PropTypes.shape({
            // list: PropTypes.array,
            config: PropTypes.func.isRequired,
        }),
    ).isRequired,
    defaultValue: PropTypes.object,
    mode: PropTypes.oneOf(['create', 'edit']),
    onSubmit: PropTypes.func,
    onClose: PropTypes.func,
    children: PropTypes.func,
};

export default withLoader(MissionForm);
